import { CheckCircle2, ClipboardCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { EnrichedFinding, FindingStatus } from '../../types/data-integrity.js';
import { SeverityBadge } from './IntegrityBadges.js';

/** @graph-connects none */
export function ActionQueueCard({
  findings,
  onStatusChange,
}: {
  findings: EnrichedFinding[];
  onStatusChange: (finding: EnrichedFinding, newStatus: FindingStatus) => void;
}) {
  const { t } = useTranslation();

  if (findings.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <CheckCircle2
            className="h-5 w-5 text-[var(--color-success,#22c55e)]"
            aria-hidden="true"
          />
          <div>
            <div className="text-sm font-medium text-[var(--color-text-primary)]">
              {t('dataIntegrity.noActionNeeded')}
            </div>
            <div className="text-xs text-[var(--color-text-muted)]">
              {t('dataIntegrity.allHighPriorityDone')}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-4 py-3 bg-red-500/5">
        <ClipboardCheck className="h-4 w-4 text-red-500" aria-hidden="true" />
        <h3 className="text-sm font-bold text-[var(--color-text-primary)]">
          {t('dataIntegrity.actionQueueTitle', { num: String(findings.length) })}
        </h3>
      </div>
      {findings.map((finding) => (
        <div
          key={finding.id}
          className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-3 last:border-b-0"
        >
          <SeverityBadge severity={finding.severity} />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-[var(--color-accent-primary)]">{finding.entityLabel}</div>
            <p className="text-sm text-[var(--color-text-primary)] line-clamp-1">
              {finding.description}
            </p>
          </div>
          <div className="shrink-0 text-right text-xs text-[var(--color-text-muted)]">
            {finding.ageHours < 24
              ? `${String(finding.ageHours)}h`
              : `${String(Math.floor(finding.ageHours / 24))}d`}
          </div>
          <div className="shrink-0 flex gap-1">
            <button
              type="button"
              onClick={() => {
                onStatusChange(finding, 'acknowledged');
              }}
              className="rounded bg-yellow-500/10 px-2 py-1 text-xs text-yellow-600 hover:bg-yellow-500/20"
            >
              {t('dataIntegrity.acknowledge')}
            </button>
            <button
              type="button"
              onClick={() => {
                onStatusChange(finding, 'in_progress');
              }}
              className="rounded bg-blue-500/10 px-2 py-1 text-xs text-blue-600 hover:bg-blue-500/20"
            >
              {t('dataIntegrity.startWork')}
            </button>
          </div>
        </div>
      ))}
    </Card>
  );
}
